import { useEffect, useState } from "react";
import { Beaker, Printer, CheckCircle2, RefreshCw, FileText, AlertCircle } from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminBulkReceive() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [autoNumber, setAutoNumber] = useState(true);
  const [manualNumbers, setManualNumbers] = useState({});
  const [conditionNotes, setConditionNotes] = useState({});
  const [batchNotes, setBatchNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const { data } = await api.get("/lab/bulk/pending");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    setSelected(selected.size === items.length ? new Set() : new Set(items.map((i) => i.test.id)));
  };

  const receive = async () => {
    if (selected.size === 0) {
      toast.error("Select at least one sample to receive");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        auto_number: autoNumber,
        internal_notes: batchNotes || undefined,
        items: Array.from(selected).map((id) => ({
          test_id: id,
          sample_number: autoNumber ? undefined : manualNumbers[id],
          sample_condition_notes: conditionNotes[id] || undefined,
        })),
      };
      const { data } = await api.post("/lab/bulk/receive", payload);
      if (data.count_received > 0) toast.success(`${data.count_received} sample${data.count_received === 1 ? "" : "s"} received`);
      if (data.count_errors > 0) toast.error(`${data.count_errors} failed — see console`);
      if (data.errors?.length) console.warn("Bulk receive errors", data.errors);
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bulk receive failed");
    } finally {
      setSubmitting(false);
    }
  };

  const printLabels = () => {
    if (selected.size === 0) {
      toast.error("Select samples to print labels for");
      return;
    }
    const ids = Array.from(selected).join(",");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/lab/bulk/labels?ids=${ids}`;
    // Open via fetch + blob so the Authorization header is sent
    const token = localStorage.getItem("scwt_token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        window.open(u, "_blank");
      })
      .catch(() => toast.error("Could not generate labels"));
  };

  const exportCsv = () => {
    if (items.length === 0) return;
    const rows = items.filter((i) => selected.has(i.test.id) || selected.size === 0).map(({ test, property, customer }) => ([
      test.booking_reference || "", test.sample_number || "", customer?.name || "",
      customer?.email || "", property?.address || "", test.test_name || "", test.status,
    ]));
    const csv = [["Booking ref","Sample no","Customer","Email","Address","Test type","Status"], ...rows]
      .map((r) => r.map((c) => `"${(c || "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `batch-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  return (
    <PortalShell title="Bulk sample receive" subtitle="Laboratory · Batch intake">
      <div className="mb-5 rounded-2xl bg-cyan-50 border border-cyan-200 p-5">
        <div className="text-sm font-semibold text-cyan-900">{items.length} samples awaiting receipt</div>
        <p className="text-xs text-cyan-800 mt-1">Tick the rows you've received together, set the numbering mode, then click <strong>Receive selected</strong>. Sample numbers are auto-generated in the format <code className="bg-cyan-100 px-1 rounded">SCWT-1001</code>, <code className="bg-cyan-100 px-1 rounded">SCWT-1002</code>… (sequential, no duplicates).</p>
      </div>

      <div className="rounded-3xl bg-white border border-slate-200 p-5 mb-5">
        <div className="flex flex-wrap items-center gap-4 mb-1">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <Checkbox data-testid="bulk-auto-number" checked={autoNumber} onCheckedChange={(v) => setAutoNumber(!!v)} />
            <span className="text-sm font-medium text-slate-800">Auto-generate sample numbers (SCWT-1001 series)</span>
          </label>
        </div>
        <div className="mt-3">
          <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Batch internal notes (applied to every received sample)</Label>
          <Textarea data-testid="bulk-batch-notes" value={batchNotes} onChange={(e) => setBatchNotes(e.target.value)} placeholder="Optional — e.g. 'Box A delivered by courier · 9 May 26 · all containers intact'" className="mt-1.5 rounded-xl" />
        </div>
        <div className="mt-4 flex flex-wrap gap-3 justify-end">
          <Button data-testid="bulk-export-csv" variant="outline" onClick={exportCsv} className="rounded-full font-semibold">
            <FileText className="w-4 h-4 mr-1.5" /> Export list
          </Button>
          <Button data-testid="bulk-print-labels" variant="outline" onClick={printLabels} className="rounded-full font-semibold" disabled={selected.size === 0}>
            <Printer className="w-4 h-4 mr-1.5" /> Print labels ({selected.size})
          </Button>
          <Button data-testid="bulk-receive-btn" onClick={receive} disabled={submitting || selected.size === 0} className="btn-aqua rounded-full font-semibold">
            <Beaker className="w-4 h-4 mr-1.5" /> Receive selected ({selected.size})
          </Button>
        </div>
      </div>

      <div className="rounded-3xl bg-white border border-slate-200 overflow-hidden">
        {items.length === 0 ? (
          <div className="p-10 text-center text-slate-500"><CheckCircle2 className="w-6 h-6 mx-auto text-emerald-500 mb-2" />All caught up — no pending samples right now.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="p-4 w-10"><Checkbox data-testid="bulk-select-all" checked={selected.size === items.length && items.length > 0} onCheckedChange={toggleAll} /></th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Booking ref</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Customer</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Property</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Test</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">{autoNumber ? "Auto sample number" : "Manual sample number"}</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Condition notes</th>
                </tr>
              </thead>
              <tbody>
                {items.map(({ test, property, customer }) => {
                  const picked = selected.has(test.id);
                  return (
                    <tr key={test.id} data-testid={`bulk-row-${test.id}`} className={`border-t border-slate-100 ${picked ? "bg-cyan-50/40" : "hover:bg-slate-50/50"}`}>
                      <td className="p-4"><Checkbox data-testid={`bulk-check-${test.id}`} checked={picked} onCheckedChange={() => toggle(test.id)} /></td>
                      <td className="p-4 font-mono text-xs font-bold text-slate-900">{test.booking_reference}</td>
                      <td className="p-4">
                        <div className="font-medium text-slate-900 text-sm">{customer?.name || "—"}</div>
                        <div className="text-xs text-slate-500">{customer?.email || ""}</div>
                      </td>
                      <td className="p-4 text-xs text-slate-600 max-w-[200px] truncate" title={property?.address}>{property?.address || "—"}</td>
                      <td className="p-4 text-slate-700">{test.test_name}</td>
                      <td className="p-4">
                        {autoNumber ? (
                          <span className="text-xs text-slate-400 italic">Auto on receive</span>
                        ) : (
                          <Input data-testid={`bulk-num-${test.id}`} value={manualNumbers[test.id] || ""} onChange={(e) => setManualNumbers({ ...manualNumbers, [test.id]: e.target.value })} placeholder="SCWT-XXXX" className="h-9 text-xs rounded-lg w-32" />
                        )}
                      </td>
                      <td className="p-4">
                        <Input data-testid={`bulk-cond-${test.id}`} value={conditionNotes[test.id] || ""} onChange={(e) => setConditionNotes({ ...conditionNotes, [test.id]: e.target.value })} placeholder="optional" className="h-9 text-xs rounded-lg" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </PortalShell>
  );
}
